import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Button, Cluster, Drawer, Spinner, useToast } from "@salesapp/ui";
import { reportSaveFailure, reportSaved } from "../../../lib/saveNotice";
import { ColorSwatchRow, EditInput, FieldPicker, PositionGrid, Toggle } from "./DesignerControls";
import { CHART_COLOR_SCHEME_OPTIONS, resolveSchemeColors } from "./chartColorSchemes";
import { FIELD_HINTS } from "../../../help/fieldHints";
import { useLocale } from "../../../shell/LocaleProvider";
import "./designers.css";
import "./dashboard_designer.css";
import {
  dashboardDesignerApi, blankLayout, newSectionId, newItemId,
  SECTION_TYPE_LABELS, CHART_TYPE_LABELS, AGGREGATION_LABELS,
  FIELD_GROUP_LABELS, FIELD_GROUP_ORDER,
} from "./api";
import type {
  DashboardTemplate, DashboardLayout, DashboardSection, DashboardSectionType, DashboardChartType,
  DashboardKpiItem, DashboardChartItem, DashboardTableItem, DashboardTextItem, DashboardSourceInfo,
} from "./api";
import { SharePanel } from "./SharePanel";

type EditTarget = { sectionId: string; itemId: string };

const SECTION_PALETTE: { type: DashboardSectionType; label: string; description: string }[] = [
  { type: "kpi", label: "KPI Cards", description: "Up to 7 metric cards" },
  { type: "charts", label: "Chart Row", description: "20 chart types, full Configure parity" },
  { type: "table", label: "Table", description: "Configurable column list" },
  { type: "text", label: "Text Box", description: "Rich text" },
];

const CHART_TYPE_PALETTE: { type: DashboardChartType; icon: string; label: string }[] = [
  { type: "funnel", icon: "\u{25BD}", label: "Funnel" },
  { type: "waterfall", icon: "\u{2195}", label: "Waterfall" },
  { type: "bar", icon: "\u{1F4CA}", label: "Bar" },
  { type: "bar_horizontal", icon: "\u{1F4F6}", label: "Bar (horizontal)" },
  { type: "bar_clustered", icon: "\u{25A4}", label: "Bar (clustered)" },
  { type: "bar_stacked", icon: "\u{25A5}", label: "Bar (stacked)" },
  { type: "bar_double", icon: "\u{21C4}", label: "Double bar" },
  { type: "line", icon: "\u{1F4C8}", label: "Line" },
  { type: "area", icon: "\u{1F3D4}", label: "Area" },
  { type: "pie", icon: "\u{1F967}", label: "Pie" },
  { type: "doughnut", icon: "\u{25EF}", label: "Doughnut" },
  { type: "scatter", icon: "\u{2058}", label: "Scatter" },
  { type: "bubble", icon: "\u{25CF}", label: "Bubble" },
  { type: "heatmap_bar", icon: "\u{25A6}", label: "Heatmap (bar-based)" },
  { type: "heatmap_grid", icon: "\u{25A7}", label: "Heatmap (grid)" },
  { type: "calendar", icon: "\u{1F4C5}", label: "Calendar" },
  { type: "word_cloud", icon: "\u{2601}", label: "Word cloud" },
  { type: "tornado", icon: "\u{29D3}", label: "Tornado" },
  { type: "sparkline", icon: "\u{FE49}", label: "Micro chart (sparkline)" },
  { type: "gauge", icon: "\u{25D1}", label: "Gauge (speedometer)" },
  { type: "number_trend", icon: "\u{1F522}", label: "Number + trend" },
];

export function DashboardDesignerPage({ orgId, listPath = "/apps/crm/dashboard-designer" }: { orgId: string; listPath?: string }) {
  const { show } = useToast();
  const { dashboardId } = useParams();
  const navigate = useNavigate();

  const [template, setTemplate] = useState<DashboardTemplate>();
  const [layout, setLayout] = useState<DashboardLayout>();
  const [source, setSource] = useState<DashboardSourceInfo>();
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<EditTarget | null>(null);
  const [fieldSearch, setFieldSearch] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => new Set(["flex"]));

  useEffect(() => {
    if (!orgId || !dashboardId) return;
    void dashboardDesignerApi.fieldCatalog(orgId).then(res => {
      setSource(Object.values(res.sources)[0]);
    }).catch(() => {});
    void dashboardDesignerApi.get(orgId, dashboardId).then(t => {
      setTemplate(t);
      setLayout(t.layout_json && t.layout_json.sections?.length ? t.layout_json : blankLayout(t.name));
    }).catch(e => setError((e as Error).message));
  }, [orgId, dashboardId]);

  if (!orgId || !dashboardId) return <Spinner />;
  if (error && !layout) return <p role="alert">{error}</p>;
  if (!layout || !template) return <Spinner />;

  function patch(fn: (l: DashboardLayout) => DashboardLayout) {
    setLayout(prev => prev ? fn(prev) : prev);
  }

  function defaultGroupBy() {
    return source?.groupable_fields.find(f => f.startsWith("opportunity."))
      ?? source?.groupable_fields.find(f => f.startsWith("lead."))
      ?? source?.groupable_fields[0]
      ?? "";
  }

  function defaultDataset() {
    return source?.datasets?.[0]?.key ?? "lead";
  }

  function addSection(type: DashboardSectionType) {
    const section: DashboardSection = { id: newSectionId(), type, title: SECTION_TYPE_LABELS[type], items: [] };
    if (type === "kpi") {
      section.items = [{ id: newItemId("kpi"), label: "New metric", aggregation: "count", column: null, dateField: null, filters: [], width: 1, dataset: defaultDataset() } as DashboardKpiItem];
    } else if (type === "charts") {
      section.items = [{ id: newItemId("chart"), title: "New chart", chartType: "bar", groupBy: defaultGroupBy(), aggregation: "count", column: null, colorScheme: "pbi-electric" } as DashboardChartItem];
    } else if (type === "table") {
      section.items = [{ id: newItemId("table"), title: "New table", columns: [defaultGroupBy()].filter(Boolean) } as DashboardTableItem];
    } else {
      section.items = [{ id: newItemId("text"), text: "" } as DashboardTextItem];
    }
    patch(l => ({ ...l, sections: [...l.sections, section] }));
  }

  function updateSection(id: string, fn: (s: DashboardSection) => DashboardSection) {
    patch(l => ({ ...l, sections: l.sections.map(s => s.id === id ? fn(s) : s) }));
  }

  function removeSection(id: string) {
    patch(l => ({ ...l, sections: l.sections.filter(s => s.id !== id) }));
    setEditing(prev => prev?.sectionId === id ? null : prev);
  }

  function moveSection(id: string, direction: -1 | 1) {
    patch(l => {
      const idx = l.sections.findIndex(s => s.id === id);
      const target = idx + direction;
      if (idx < 0 || target < 0 || target >= l.sections.length) return l;
      const next = [...l.sections];
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...l, sections: next };
    });
  }

  function addItem(sectionId: string) {
    updateSection(sectionId, s => {
      let item;
      if (s.type === "kpi") {
        item = { id: newItemId("kpi"), label: "New metric", aggregation: "count", column: null, dateField: null, filters: [], width: 1, dataset: defaultDataset() } as DashboardKpiItem;
      } else if (s.type === "charts") {
        item = { id: newItemId("chart"), title: "New chart", chartType: "bar", groupBy: defaultGroupBy(), aggregation: "count", column: null, colorScheme: "pbi-electric" } as DashboardChartItem;
      } else if (s.type === "table") {
        item = { id: newItemId("table"), title: "New table", columns: [defaultGroupBy()].filter(Boolean) } as DashboardTableItem;
      } else {
        item = { id: newItemId("text"), text: "" } as DashboardTextItem;
      }
      return { ...s, items: [...s.items, item] };
    });
  }

  function removeItem(sectionId: string, itemId: string) {
    updateSection(sectionId, s => ({ ...s, items: s.items.filter(i => i.id !== itemId) }));
    setEditing(prev => prev?.sectionId === sectionId && prev.itemId === itemId ? null : prev);
  }

  function moveItem(sectionId: string, itemId: string, direction: -1 | 1) {
    updateSection(sectionId, s => {
      const idx = s.items.findIndex(i => i.id === itemId);
      const target = idx + direction;
      if (idx < 0 || target < 0 || target >= s.items.length) return s;
      const next = [...s.items];
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...s, items: next };
    });
  }

  function updateItem(sectionId: string, itemId: string, fn: (i: DashboardSection["items"][number]) => DashboardSection["items"][number]) {
    updateSection(sectionId, s => ({ ...s, items: s.items.map(i => i.id === itemId ? fn(i) : i) }));
  }

  async function save() {
    if (!orgId || !dashboardId || !layout) return;
    setSaving(true); setError("");
    try {
      await dashboardDesignerApi.update(orgId, dashboardId, { name: layout.title, layout_json: layout });
      reportSaved(show, "Dashboard saved.");
      navigate(listPath);
    } catch (e) { setError(reportSaveFailure(show, e, "Dashboard could not be saved.")); }
    finally { setSaving(false); }
  }

  const editingSection = editing ? layout.sections.find(s => s.id === editing.sectionId) : undefined;
  const editingItem = editingSection?.items.find(i => i.id === editing?.itemId);
  const allFields = source?.fields ?? [];
  const searchTerm = fieldSearch.trim().toLowerCase();
  const visibleFields = searchTerm
    ? allFields.filter(f => f.label.toLowerCase().includes(searchTerm) || f.key.toLowerCase().includes(searchTerm))
    : allFields;
  const fieldsByGroup = FIELD_GROUP_ORDER.map(group => ({
    group,
    fields: visibleFields.filter(f => f.group === group),
  })).filter(g => g.fields.length > 0);

  function toggleGroup(group: string) {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group); else next.add(group);
      return next;
    });
  }

  return (
    <div className="designer">
      <Cluster justify="space-between" align="center">
        <h1>{`Designing: ${layout.title}`}</h1>
        <Cluster gap={2}>
          <Button
            variant="secondary"
            title="Leave the designer without saving. Unsaved layout changes are discarded."
            onClick={() => navigate(listPath)}
          >
            Cancel
          </Button>
          <Button
            disabled={saving}
            title="Save this dashboard’s layout, sections, and chart settings."
            onClick={() => save()}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </Cluster>
      </Cluster>
      {error ? <p role="alert" className="solution-error">{error}</p> : null}

      <div className="dashboard-designer__layout">
        <aside className="dashboard-designer__sidebar">
          <div className="dashboard-designer__palette">
            <h2>Add section</h2>
            {SECTION_PALETTE.map(entry => (
              <button
                key={entry.type}
                type="button"
                className="dashboard-designer__palette-item"
                title={`Add a ${entry.label} section: ${entry.description}`}
                onClick={() => addSection(entry.type)}
              >
                <span className="dashboard-designer__palette-item-label">{entry.label}</span>
                <span className="dashboard-designer__palette-item-description">{entry.description}</span>
              </button>
            ))}
          </div>

          <div className="dashboard-designer__palette">
            <h2>Chart types</h2>
            <div className="dashboard-designer__chart-types">
              {CHART_TYPE_PALETTE.map(entry => (
                <div key={entry.type} className="dashboard-designer__chart-type-item">
                  <span className="dashboard-designer__chart-type-item-icon">{entry.icon}</span>
                  <span>{entry.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="dashboard-designer__source">
            <h2>Data source</h2>
            {source ? (
              <>
                <div className="dashboard-designer__source-name">{source.label}</div>
                <div className="dashboard-designer__source-count">{allFields.length} fields</div>
                <input
                  type="search"
                  className="dashboard-designer__source-search"
                  placeholder="Search fields…"
                  value={fieldSearch}
                  onChange={e => setFieldSearch(e.target.value)}
                />
                <div className="dashboard-designer__source-scroll">
                  {fieldsByGroup.length === 0 ? (
                    <p className="dashboard-designer__source-empty">No fields match "{fieldSearch}".</p>
                  ) : fieldsByGroup.map(({ group, fields }) => {
                    const collapsed = !searchTerm && collapsedGroups.has(group);
                    return (
                      <div key={group} className="dashboard-designer__source-group">
                        <button
                          type="button"
                          className="dashboard-designer__source-group-header"
                          title={collapsed ? `Expand ${FIELD_GROUP_LABELS[group] || group} fields` : `Collapse ${FIELD_GROUP_LABELS[group] || group} fields`}
                          onClick={() => toggleGroup(group)}
                        >
                          <span>{FIELD_GROUP_LABELS[group] || group} <span className="dashboard-designer__source-group-count">({fields.length})</span></span>
                          <span>{collapsed ? "▸" : "▾"}</span>
                        </button>
                        {!collapsed && (
                          <ul className="dashboard-designer__source-fields">
                            {fields.map(f => (
                              <li key={f.key} className="dashboard-designer__source-field">
                                <span>{f.label}</span>
                                <span className="dashboard-designer__source-field-kind">{f.kind}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : <Spinner />}
          </div>
        </aside>

        <div className="dashboard-designer__canvas">
          <section className="designer-guidance">
            <EditInput label="Dashboard title" value={layout.title} onChange={title => patch(l => ({ ...l, title }))} />
            <EditInput label="Eyebrow" value={layout.eyebrow || ""} onChange={eyebrow => patch(l => ({ ...l, eyebrow }))} />
          </section>

          {layout.sections.map((section, sIdx) => (
            <div key={section.id} className="dashboard-designer__section">
              <Cluster justify="space-between" align="center" className="dashboard-designer__section-header">
                <EditInput label="Section title" value={section.title} onChange={title => updateSection(section.id, s => ({ ...s, title }))} />
                <Cluster gap={1}>
                  <Button
                    variant="secondary"
                    disabled={sIdx === 0}
                    title="Move this section up."
                    aria-label="Move section up"
                    onClick={() => moveSection(section.id, -1)}
                  >
                    ▲
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={sIdx === layout.sections.length - 1}
                    title="Move this section down."
                    aria-label="Move section down"
                    onClick={() => moveSection(section.id, 1)}
                  >
                    ▼
                  </Button>
                  <Button
                    variant="destructive"
                    title={`Remove the “${section.title || SECTION_TYPE_LABELS[section.type]}” section and everything in it.`}
                    onClick={() => removeSection(section.id)}
                  >
                    Delete
                  </Button>
                </Cluster>
              </Cluster>

              {section.type === "text" ? (
                <EditInput label="Text" type="textarea" value={(section.items[0] as DashboardTextItem | undefined)?.text || ""}
                  onChange={text => updateSection(section.id, s => ({ ...s, items: [{ id: s.items[0]?.id || newItemId("text"), text }] }))} />
              ) : (
                <>
                  {section.items.map((item, iIdx) => (
                    <div key={item.id} className="dashboard-designer__item-row">
                      <span className="dashboard-designer__item-summary">{itemSummary(section, item)}</span>
                      <Cluster gap={1}>
                        <Button
                          variant="secondary"
                          disabled={iIdx === 0}
                          title="Move this item left in the section."
                          aria-label="Move item left"
                          onClick={() => moveItem(section.id, item.id, -1)}
                        >
                          ◀
                        </Button>
                        <Button
                          variant="secondary"
                          disabled={iIdx === section.items.length - 1}
                          title="Move this item right in the section."
                          aria-label="Move item right"
                          onClick={() => moveItem(section.id, item.id, 1)}
                        >
                          ▶
                        </Button>
                        <Button
                          variant="secondary"
                          title="Open settings for this item (fields, chart type, colours, position)."
                          onClick={() => setEditing({ sectionId: section.id, itemId: item.id })}
                        >
                          Configure
                        </Button>
                        <Button
                          variant="destructive"
                          title="Remove this item from the section."
                          onClick={() => removeItem(section.id, item.id)}
                        >
                          Delete
                        </Button>
                      </Cluster>
                    </div>
                  ))}
                  <Button
                    variant="secondary"
                    title={
                      section.type === "kpi"
                        ? "Add another KPI card to this section."
                        : section.type === "charts"
                          ? "Add another chart to this section."
                          : "Add another column to this table section."
                    }
                    onClick={() => addItem(section.id)}
                  >
                    + {section.type === "kpi" ? "Add KPI" : section.type === "charts" ? "Add chart" : "Add column"}
                  </Button>
                </>
              )}
            </div>
          ))}

          {layout.sections.length === 0 && (
            <p className="dashboard-designer__empty-canvas">
              Add a section from the palette on the left to start building this dashboard.
            </p>
          )}

          <SharePanel orgId={orgId} dashboardId={dashboardId} />
        </div>
      </div>

      <Drawer
        className="designer"
        open={Boolean(editing && editingSection && editingItem)}
        onClose={() => setEditing(null)}
        title={editingSection ? `Configure: ${SECTION_TYPE_LABELS[editingSection.type]}` : "Configure"}
        footer={
          <Button title="Close this panel. Changes stay in the designer until you Save." onClick={() => setEditing(null)}>
            Done
          </Button>
        }
      >
        {editingSection && editingItem && (
          <ItemConfigForm
            section={editingSection}
            item={editingItem}
            source={source}
            onChange={fn => updateItem(editingSection.id, editingItem.id, fn)}
          />
        )}
      </Drawer>
    </div>
  );
}

function itemSummary(section: DashboardSection, item: DashboardSection["items"][number]): string {
  if (section.type === "kpi") {
    const kpi = item as DashboardKpiItem;
    return `${kpi.label} — ${AGGREGATION_LABELS[kpi.aggregation]}`;
  }
  if (section.type === "charts") {
    const chart = item as DashboardChartItem;
    return `${chart.title} — ${CHART_TYPE_LABELS[chart.chartType]} · grouped by ${chart.groupBy || "—"}`;
  }
  const table = item as DashboardTableItem;
  return `${table.title} — ${table.columns.length} column${table.columns.length === 1 ? "" : "s"}`;
}

function ItemConfigForm({ section, item, source, onChange }: {
  section: DashboardSection;
  item: DashboardSection["items"][number];
  source?: DashboardSourceInfo;
  onChange: (fn: (i: DashboardSection["items"][number]) => DashboardSection["items"][number]) => void;
}) {
  if (section.type === "kpi") {
    const kpi = item as DashboardKpiItem;
    const datasetOptions = source?.datasets?.map(d => ({ value: d.key, label: d.label })) || [];
    const numericOptions = (source?.numeric_fields || [])
      .filter(f => !kpi.dataset || f.startsWith(`${kpi.dataset}.`))
      .map(f => ({ value: f, label: source?.field_labels[f] || f }));
    const dateOptions = (source?.date_fields || [])
      .filter(f => !kpi.dataset || f.startsWith(`${kpi.dataset}.`))
      .map(f => ({ value: f, label: source?.field_labels[f] || f }));
    return (
      <>
        <EditInput label="Label" value={kpi.label} onChange={label => onChange(i => ({ ...(i as DashboardKpiItem), label }))} />
        <FieldPicker label="Data source" tooltip={FIELD_HINTS.dashboard_kpi_dataset} value={kpi.dataset ?? ""} options={datasetOptions}
          onChange={dataset => onChange(i => ({ ...(i as DashboardKpiItem), dataset, column: null, dateField: null }))} />
        <EditInput label="Aggregation" tooltip={FIELD_HINTS.dashboard_chart_aggregation} value={kpi.aggregation} options={Object.entries(AGGREGATION_LABELS).map(([value, label]) => ({ value, label }))}
          onChange={aggregation => onChange(i => ({ ...(i as DashboardKpiItem), aggregation: aggregation as DashboardKpiItem["aggregation"] }))} />
        {kpi.aggregation !== "count" && (
          <FieldPicker label="Value field" tooltip={FIELD_HINTS.dashboard_chart_value_field} value={kpi.column ?? ""} options={numericOptions}
            allowBlank="(required for this aggregation)" onChange={column => onChange(i => ({ ...(i as DashboardKpiItem), column: column || null }))} />
        )}
        <EditInput label="Date field" tooltip={FIELD_HINTS.dashboard_chart_date_field} value={kpi.dateField ?? ""} options={[{ value: "", label: "None" }, ...dateOptions]}
          onChange={dateField => onChange(i => ({ ...(i as DashboardKpiItem), dateField: dateField || null }))} />
        <EditInput label="Width" tooltip={FIELD_HINTS.dashboard_kpi_width} value={String(kpi.width || 1)} options={[1, 2, 3, 4, 5, 6, 7].map(n => ({ value: String(n), label: `${n} / 7 columns` }))}
          onChange={width => onChange(i => ({ ...(i as DashboardKpiItem), width: Number(width) }))} />
      </>
    );
  }
  if (section.type === "charts") {
    return <ChartItemConfigFields chart={item as DashboardChartItem} source={source} onChange={onChange} />;
  }
  const table = item as DashboardTableItem;
  const tableEntity = table.columns.find(c => c.includes("."))?.split(".")[0];
  const allColumns = (source ? [...source.groupable_fields, ...source.date_fields, ...source.numeric_fields] : [])
    .filter(col => !tableEntity || col.startsWith(`${tableEntity}.`));
  return (
    <>
      <EditInput label="Title" value={table.title} onChange={title => onChange(i => ({ ...(i as DashboardTableItem), title }))} />
      <p className="dashboard-designer__source-empty">{FIELD_HINTS.dashboard_table_columns}</p>
      {allColumns.map(col => (
        <label key={col} className="dashboard-designer__column-toggle">
          <input
            type="checkbox"
            checked={table.columns.includes(col)}
            onChange={e => onChange(i => {
              const t = i as DashboardTableItem;
              return { ...t, columns: e.target.checked ? [...t.columns, col] : t.columns.filter(c => c !== col) };
            })}
          />
          {source?.field_labels[col] || col}
        </label>
      ))}
    </>
  );
}

const VALUE_FORMAT_OPTIONS = [
  { value: "actual", label: "Actual value" },
  { value: "percent", label: "Percentage of total" },
  { value: "money", label: "Money" },
];

function ChartItemConfigFields({ chart, source, onChange }: {
  chart: DashboardChartItem;
  source?: DashboardSourceInfo;
  onChange: (fn: (i: DashboardSection["items"][number]) => DashboardSection["items"][number]) => void;
}) {
  const { orgCurrency, orgCurrencySymbol } = useLocale();
  const valueFormats = VALUE_FORMAT_OPTIONS.map(option => (
    option.value === "money" ? { ...option, label: `Money (${orgCurrencySymbol})` } : option
  ));
  function set(patch: Partial<DashboardChartItem>) {
    onChange(i => ({ ...(i as DashboardChartItem), ...patch }));
  }

  const entity = chart.groupBy?.includes(".") ? chart.groupBy.split(".")[0] : "";
  const groupableOptions = [
    ...(source?.groupable_fields?.map(f => ({ value: f, label: source.field_labels[f] || f })) || []),
    ...(source?.date_fields?.map(f => ({ value: f, label: `${source.field_labels[f] || f} (by day)` })) || []),
  ];
  const numericOptions = (source?.numeric_fields || [])
    .filter(f => !entity || f.startsWith(`${entity}.`))
    .map(f => ({ value: f, label: source?.field_labels[f] || f }));
  const dateOptions = (source?.date_fields || [])
    .filter(f => !entity || f.startsWith(`${entity}.`))
    .map(f => ({ value: f, label: source?.field_labels[f] || f }));
  const isBar = chart.chartType === "bar";

  return (
    <>
      <EditInput label="Title" value={chart.title} onChange={title => set({ title })} />
      <ColorSwatchRow label="Title colour" value={chart.titleColor} onChange={titleColor => set({ titleColor })} />
      <EditInput label="Chart type" tooltip={FIELD_HINTS.dashboard_chart_type} value={chart.chartType} options={Object.entries(CHART_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
        onChange={chartType => set({ chartType: chartType as DashboardChartItem["chartType"] })} />
      {isBar && (
        <div className="designer-input">
          <span>Orientation</span>
          <Cluster gap={2}>
            <Button type="button" variant={(!chart.orientation || chart.orientation === "vertical") ? "primary" : "secondary"}
              title="Draw bars upward from the category axis."
              onClick={() => set({ orientation: "vertical" })}>Vertical</Button>
            <Button type="button" variant={chart.orientation === "horizontal" ? "primary" : "secondary"}
              title="Draw bars sideways from the category axis."
              onClick={() => set({ orientation: "horizontal" })}>Horizontal</Button>
          </Cluster>
        </div>
      )}
      <FieldPicker label="Value field" tooltip={FIELD_HINTS.dashboard_chart_value_field} value={chart.valueField ?? chart.column ?? ""} options={numericOptions}
        allowBlank="(none — plain count)" onChange={valueField => set({ valueField: valueField || null, column: valueField || null })} />
      <FieldPicker label="Group by field" tooltip={FIELD_HINTS.dashboard_chart_group_by} value={chart.groupBy} options={groupableOptions}
        onChange={groupBy => {
          const nextEntity = groupBy.includes(".") ? groupBy.split(".")[0] : "";
          const valueField = chart.valueField ?? chart.column;
          const keepValue = Boolean(valueField && nextEntity && valueField.startsWith(`${nextEntity}.`));
          const keepDate = Boolean(chart.dateField && nextEntity && chart.dateField.startsWith(`${nextEntity}.`));
          set({
            groupBy,
            valueField: keepValue ? valueField : null,
            column: keepValue ? valueField : null,
            dateField: keepDate ? chart.dateField : null,
          });
        }} />
      <EditInput label="Aggregation" tooltip={FIELD_HINTS.dashboard_chart_aggregation} value={chart.aggregation} options={Object.entries(AGGREGATION_LABELS).map(([value, label]) => ({ value, label }))}
        onChange={aggregation => set({ aggregation: aggregation as DashboardChartItem["aggregation"] })} />
      <EditInput label="Date field" tooltip={FIELD_HINTS.dashboard_chart_date_field} value={chart.dateField ?? ""} options={[{ value: "", label: "None" }, ...dateOptions]}
        onChange={dateField => set({ dateField: dateField || null })} />
      <EditInput label="Field display name" tooltip={FIELD_HINTS.dashboard_field_display_name} value={chart.fieldDisplayName ?? ""} disabled={!chart.groupBy}
        onChange={fieldDisplayName => set({ fieldDisplayName })}>
        <small>Optional — overrides the raw field name shown on axes/legends.</small>
      </EditInput>
      <EditInput label="Axis unit of measure" tooltip={FIELD_HINTS.dashboard_axis_unit} value={chart.axisUnit ?? ""} onChange={axisUnit => set({ axisUnit })}>
        <small>Optional. $ is money and shows as {orgCurrencySymbol} ({orgCurrency}). Other units, such as hrs or kg, stay as typed.</small>
      </EditInput>
      <EditInput label="Colour scheme" tooltip={FIELD_HINTS.dashboard_chart_color_scheme} value={chart.colorScheme ?? "pbi-electric"} options={[...CHART_COLOR_SCHEME_OPTIONS]}
        onChange={colorScheme => set({ colorScheme: colorScheme as DashboardChartItem["colorScheme"] })} />
      <div className="designer-scheme-swatches" aria-hidden="true">
        {resolveSchemeColors(chart.colorScheme).map((c, i) => (
          <span key={i} className="designer-swatch" style={{ backgroundColor: c }} />
        ))}
      </div>
      <div className="designer-input">
        <span>Display options</span>
        <Toggle label="Show legend" tooltip={FIELD_HINTS.dashboard_chart_show_legend} checked={chart.showLegend ?? true} onChange={showLegend => set({ showLegend })} />
        <Toggle label="Show values" tooltip={FIELD_HINTS.dashboard_chart_show_values} checked={chart.showValues} onChange={showValues => set({ showValues })} />
        {chart.showValues && (
          <EditInput label="Display format" tooltip={FIELD_HINTS.dashboard_chart_value_format} value={chart.valueFormat ?? "actual"} options={valueFormats}
            onChange={valueFormat => set({ valueFormat: valueFormat as DashboardChartItem["valueFormat"] })} />
        )}
      </div>
      <EditInput label="Tooltip" value={chart.tooltip ?? ""} onChange={tooltip => set({ tooltip })}>
        <small>Optional hover text.</small>
      </EditInput>
      <PositionGrid colStart={chart.colStart} colSpan={chart.colSpan}
        onChange={({ colStart, colSpan }) => set({ colStart, colSpan })} />
    </>
  );
}
